import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ScrollArea } from './ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { 
    Bell, BellRing, Check, X, Settings, Trash2, 
    Calendar, Trophy, Smile, Coffee, Moon, Droplets
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { de, enUS } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_ICONS = {
    challenge_day_1: Trophy,
    challenge_day_3: Trophy,
    challenge_day_5: Trophy,
    challenge_day_7: Trophy,
    challenge_complete: Trophy,
    challenge_reminder: Bell,
    weekly_report: Calendar,
    mood_reminder: Smile,
    streak_milestone: Trophy,
    morning_motivation: Coffee,
    test: Bell,
};

const TYPE_COLORS = {
    challenge_day_1: 'text-amber-500',
    challenge_day_3: 'text-amber-500',
    challenge_day_5: 'text-amber-500',
    challenge_day_7: 'text-green-500',
    challenge_complete: 'text-green-500',
    weekly_report: 'text-violet-500',
    mood_reminder: 'text-pink-500',
    streak_milestone: 'text-orange-500',
    morning_motivation: 'text-yellow-500',
    test: 'text-blue-500',
};

export function NotificationBell() {
    const { language } = useLanguage();
    const locale = language === 'de' ? de : enUS;
    const [open, setOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [subscribed, setSubscribed] = useState(false);

    const t = {
        de: {
            notifications: 'Benachrichtigungen',
            noNotifications: 'Keine Benachrichtigungen',
            markAllRead: 'Alle als gelesen markieren',
            enableNotifications: 'Push aktivieren',
            disableNotifications: 'Push deaktivieren',
            testNotification: 'Test senden',
            notificationsEnabled: 'Push-Benachrichtigungen aktiviert!',
            notificationsDisabled: 'Push-Benachrichtigungen deaktiviert',
            permissionDenied: 'Benachrichtigungen wurden im Browser blockiert'
        },
        en: {
            notifications: 'Notifications',
            noNotifications: 'No notifications',
            markAllRead: 'Mark all as read',
            enableNotifications: 'Enable Push',
            disableNotifications: 'Disable Push',
            testNotification: 'Send Test',
            notificationsEnabled: 'Push notifications enabled!',
            notificationsDisabled: 'Push notifications disabled',
            permissionDenied: 'Notifications blocked in browser'
        }
    }[language];

    useEffect(() => {
        loadNotifications();
        checkSubscriptionStatus();
    }, []);

    const loadNotifications = async () => {
        try {
            const [historyRes, countRes] = await Promise.all([
                axios.get(`${API}/api/notifications/history?limit=10`),
                axios.get(`${API}/api/notifications/unread-count`)
            ]);
            setNotifications(historyRes.data.notifications || []);
            setUnreadCount(countRes.data.unread_count || 0);
        } catch (error) {
            console.error('Failed to load notifications:', error);
        }
    };

    const checkSubscriptionStatus = async () => {
        try {
            const response = await axios.get(`${API}/api/notifications/status`);
            setSubscribed(response.data.subscribed);
        } catch (error) {
            console.error('Failed to check subscription:', error);
        }
    };

    const requestNotificationPermission = async () => {
        if (!('Notification' in window)) {
            toast.error(language === 'de' ? 'Dein Browser unterstützt keine Push-Benachrichtigungen' : 'Your browser does not support push notifications');
            return false;
        }

        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
            toast.error(t.permissionDenied);
            return false;
        }
        return true;
    };

    const handleEnableNotifications = async () => {
        setLoading(true);
        try {
            const hasPermission = await requestNotificationPermission();
            if (!hasPermission) {
                setLoading(false);
                return;
            }

            // For now, we'll just enable server-side notifications
            // In production, you'd use Web Push API with VAPID keys
            await axios.post(`${API}/api/notifications/subscribe`, {
                endpoint: `browser-${Date.now()}`,
                keys: { p256dh: 'placeholder', auth: 'placeholder' }
            });

            setSubscribed(true);
            toast.success(t.notificationsEnabled);
            
            // Show a test notification
            if (Notification.permission === 'granted') {
                new Notification('Nanouk sagt Hallo! 🐺', {
                    body: language === 'de' ? 'Push-Benachrichtigungen sind jetzt aktiv!' : 'Push notifications are now active!',
                    icon: 'https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop'
                });
            }
        } catch (error) {
            console.error('Failed to enable notifications:', error);
            toast.error(language === 'de' ? 'Fehler beim Aktivieren' : 'Failed to enable');
        } finally {
            setLoading(false);
        }
    };

    const handleDisableNotifications = async () => {
        setLoading(true);
        try {
            await axios.delete(`${API}/api/notifications/unsubscribe`);
            setSubscribed(false);
            toast.success(t.notificationsDisabled);
        } catch (error) {
            console.error('Failed to disable notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSendTest = async () => {
        try {
            const response = await axios.post(`${API}/api/notifications/test?language=${language}`, {});
            
            // Show browser notification
            if (Notification.permission === 'granted') {
                new Notification(response.data.notification.title, {
                    body: response.data.notification.body,
                    icon: response.data.notification.icon
                });
            }
            
            loadNotifications();
            toast.success(language === 'de' ? 'Test gesendet!' : 'Test sent!');
        } catch (error) {
            console.error('Failed to send test:', error);
        }
    };

    const markAsRead = async (notificationId) => {
        try {
            await axios.post(`${API}/api/notifications/mark-read/${notificationId}`, {});
            loadNotifications();
        } catch (error) {
            console.error('Failed to mark as read:', error);
        }
    };

    const markAllAsRead = async () => {
        for (const n of notifications.filter(n => !n.read)) {
            await markAsRead(n.id);
        }
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button 
                    variant="ghost" 
                    size="icon" 
                    className="relative"
                    data-testid="notification-bell"
                >
                    {unreadCount > 0 ? (
                        <motion.div
                            animate={{ rotate: [0, -10, 10, -10, 0] }}
                            transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 3 }}
                        >
                            <BellRing className="w-5 h-5" />
                        </motion.div>
                    ) : (
                        <Bell className="w-5 h-5" />
                    )}
                    {unreadCount > 0 && (
                        <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-red-500 text-white text-xs">
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </Badge>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="end">
                <Card className="border-0 shadow-none">
                    <CardHeader className="pb-2 pt-4 px-4">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-base flex items-center gap-2">
                                <Bell className="w-4 h-4" />
                                {t.notifications}
                            </CardTitle>
                            {unreadCount > 0 && (
                                <Button variant="ghost" size="sm" onClick={markAllAsRead}>
                                    <Check className="w-4 h-4 mr-1" />
                                    {t.markAllRead}
                                </Button>
                            )}
                        </div>
                    </CardHeader>
                    <CardContent className="p-0">
                        {/* Notification List */}
                        <ScrollArea className="h-64">
                            {notifications.length === 0 ? (
                                <div className="p-8 text-center text-muted-foreground">
                                    <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                    <p className="text-sm">{t.noNotifications}</p>
                                </div>
                            ) : (
                                <div className="divide-y">
                                    {notifications.map((notification) => {
                                        const Icon = TYPE_ICONS[notification.type] || Bell;
                                        const colorClass = TYPE_COLORS[notification.type] || 'text-gray-500';
                                        return (
                                            <motion.div
                                                key={notification.id}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                className={`p-3 hover:bg-muted/50 cursor-pointer ${!notification.read ? 'bg-blue-50/50 dark:bg-blue-950/20' : ''}`}
                                                onClick={() => !notification.read && markAsRead(notification.id)}
                                            >
                                                <div className="flex gap-3">
                                                    <div className={`mt-0.5 ${colorClass}`}>
                                                        <Icon className="w-5 h-5" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className={`text-sm font-medium ${!notification.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                                                            {notification.title}
                                                        </p>
                                                        <p className="text-xs text-muted-foreground line-clamp-2">
                                                            {notification.body}
                                                        </p>
                                                        <p className="text-[10px] text-muted-foreground mt-1">
                                                            {formatDistanceToNow(new Date(notification.sent_at), { addSuffix: true, locale })}
                                                        </p>
                                                    </div>
                                                    {!notification.read && (
                                                        <div className="w-2 h-2 rounded-full bg-blue-500 mt-2" />
                                                    )}
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            )}
                        </ScrollArea>

                        {/* Actions */}
                        <div className="p-3 border-t bg-muted/30 space-y-2">
                            <div className="flex gap-2">
                                {subscribed ? (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex-1"
                                        onClick={handleDisableNotifications}
                                        disabled={loading}
                                    >
                                        <X className="w-4 h-4 mr-1" />
                                        {t.disableNotifications}
                                    </Button>
                                ) : (
                                    <Button
                                        variant="default"
                                        size="sm"
                                        className="flex-1 bg-gradient-to-r from-violet-500 to-indigo-500"
                                        onClick={handleEnableNotifications}
                                        disabled={loading}
                                    >
                                        <BellRing className="w-4 h-4 mr-1" />
                                        {t.enableNotifications}
                                    </Button>
                                )}
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleSendTest}
                                >
                                    {t.testNotification}
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </PopoverContent>
        </Popover>
    );
}
